import { get } from 'lodash'
import { Suspense, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'

import AssetCookie from '@/components/AssetCookie'
import {
  isAuthenticated as getIsAuthenticated,
  getSessionExpirationMoment,
  isAuthenticationError
} from '@/components/AuthenticationHandler/selectors'
import CaptureSessionTimeout from '@/components/CaptureSessionTimeout'
import ErrorComponent from '@/components/ErrorComponent'
import ToolBarIcons from '@/components/images/ToolBarIcons'
import Loading from '@/components/Loading'
import FeedbackButtonStateProvider from '@/components/navigation/FeedbackButtonStateContext'
import NavigationContainer from '@/components/navigation/NavigationContainer'
import Page from '@/components/Page'
import PageNotFound from '@/components/PageNotFound'
import useAnalytics from '@/hooks/useAnalytics'
import LoggedOutPage from '@/pages/LoggedOutPage'
import { WorkflowValidationsProvider } from '@/pages/SpaceAppBuilder/AppDetailsPage/WorkflowValidationContext'
import AuthorizeCallbackPage from '@/pages/SpaceAppBuilder/Oauth2DataSourceAuthorizeCallbackPage/AuthorizeCallbackPage'
import { AppRoute } from '@/routes'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { isRequestComplete, isRequestError, isRequestPending } from '@/store/selectors'
import theme from '@/utilities/theme'

import {
  fetchConsoleConfiguration,
  fetchContextOrganization,
  fetchContextUser,
  fetchTenantConfiguration,
  persistReduxState
} from './actions'
import {
  FETCH_CONSOLE_CONFIGURATION,
  FETCH_CONTEXT_ORGANIZATION,
  FETCH_CONTEXT_USER,
  FETCH_ROLES,
  FETCH_TENANT_CONFIGURATION
} from './actionTypes'
import AppRoutes from './AppRoutes'
import { getCurrentSpaceId, getOrganization } from './selectors'

const errorsToHandle = [
  {
    type: FETCH_CONSOLE_CONFIGURATION,
    data: { detailCode: 'APPFCC' }
  },
  {
    type: FETCH_CONTEXT_ORGANIZATION,
    data: { detailCode: 'APPFCO' }
  },
  {
    type: FETCH_CONTEXT_USER,
    data: { detailCode: 'APPFCU' }
  },
  {
    type: FETCH_ROLES,
    data: { detailCode: 'APPFR' }
  },
  {
    type: FETCH_TENANT_CONFIGURATION,
    data: { detailCode: 'APPFTC' }
  }
]

/**
 * Detect if `react-router` was unable to match the URL. Once a route hasn't been found, state for the top of the
 * stack will continue to indicate an error even though the path has changed. As such, the action for the top of the
 * stack must be examined as well or else manually manipulating the URL to something valid will still result in the
 * route not found message displaying.
 *
 * @param action - History stack action.
 * @param state - State hashmap.
 * @return Whether the history stack indicates the page wasn't found.
 */
const isRouteNotFound = (action: string, state: object) => action === 'REPLACE' && get(state, 'notFoundError', false)

const isSuccessfulLogoutPage = (path: string) => path === '/loggedout'

// Used for oauth2 datasources flow
const isDataSourceOauth2CallbackPage = (path: string) => path === '/datasource/callback'

export type AppProps = {
  routes: AppRoute[]
}

function App({ routes }: AppProps) {
  const analytics = useAnalytics()

  const history = useHistory()
  const location = useLocation<{ notFoundError?: boolean }>()

  useEffect(() => {
    return history.listen((location, action) => {
      analytics.set('page', location.pathname + location.search)
      analytics.track('pageview', { action: action.toLowerCase() })
    })
  }, [history, analytics])

  const dispatch = useAppDispatch()

  const currentSpaceId = useAppSelector(getCurrentSpaceId)
  const isAuthenticated = useAppSelector(getIsAuthenticated)
  const isError = useAppSelector(
    state =>
      isAuthenticationError(state) ||
      isRequestError(state, [
        // This request can fail as we'll just serve up the default UI
        // FETCH_CONSOLE_CONFIGURATION,
        FETCH_CONTEXT_ORGANIZATION,
        FETCH_CONTEXT_USER,
        FETCH_ROLES,
        FETCH_TENANT_CONFIGURATION
      ])
  )
  const isLoading = useAppSelector(state =>
    isRequestPending(state, [
      FETCH_CONSOLE_CONFIGURATION,
      FETCH_CONTEXT_ORGANIZATION,
      FETCH_CONTEXT_USER,
      FETCH_ROLES,
      FETCH_TENANT_CONFIGURATION
    ])
  )
  const isTenantConfigurationLoaded = useAppSelector(state => isRequestComplete(state, FETCH_TENANT_CONFIGURATION))
  const organization = useAppSelector(getOrganization)
  const sessionExpiration = useAppSelector(getSessionExpirationMoment)

  useEffect(() => {
    if (!isTenantConfigurationLoaded && !isSuccessfulLogoutPage(location.pathname)) {
      const isAdminLogin = new URLSearchParams(location.search).has('adminLogin')
      dispatch(fetchTenantConfiguration(isAdminLogin))
    }
  }, [dispatch, isTenantConfigurationLoaded, location.pathname, location.search])

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchContextUser())
      dispatch(fetchContextOrganization())
      dispatch(fetchConsoleConfiguration(currentSpaceId))
    }
    // Only trigger if `isAuthenticated` changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isAuthenticated])

  //if the space changes, check for feature flags again.
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchConsoleConfiguration(currentSpaceId))
    }
    // Only trigger if `currentSpaceId` changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSpaceId, dispatch])

  const refreshSession = async () => {
    await dispatch(persistReduxState())
    history.push({ pathname: '/login', state: { from: { pathname: location.pathname } } })
  }

  if (isDataSourceOauth2CallbackPage(location.pathname)) {
    // capture a successful OAuth2 authorize flow
    return <AuthorizeCallbackPage />
  } else if (isSuccessfulLogoutPage(location.pathname)) {
    // capture successful log outs as they aren't a registered route
    return <LoggedOutPage />
  } else if (!isTenantConfigurationLoaded) {
    // nothing should proceed until the tenant configuration has loaded
    return <Loading />
  } else if (isError) {
    return (
      <Page errorsToHandle={errorsToHandle}>
        <ErrorComponent errors={[{ detailCode: 'ERR' }]} />
      </Page>
    )
  } else if (isRouteNotFound(history.action, location.state)) {
    return <PageNotFound />
  }

  const shouldShowNavigation = ![
    '/loggedout',
    '/login',
    '/logout',
    '/signin',
    '/signout',
    '/datasource/callback'
  ].includes(location.pathname)

  return (
    <AssetCookie isAuthenticated={isAuthenticated}>
      <ThemeProvider theme={theme}>
        <WorkflowValidationsProvider>
          <FeedbackButtonStateProvider>
            <>
              <ToolBarIcons />
              <div className="layout flex-col">
                {shouldShowNavigation && <NavigationContainer />}

                <div className="flex flex-1">
                  <Suspense fallback={<Loading />}>
                    <AppRoutes
                      currentSpaceId={currentSpaceId}
                      isAuthenticated={isAuthenticated}
                      isError={isError}
                      isLoading={isLoading}
                      isTenantConfigurationLoaded={isTenantConfigurationLoaded}
                      organization={organization}
                      routes={routes}
                    />

                    {!isLoading && (
                      <CaptureSessionTimeout
                        expirationMoment={sessionExpiration}
                        isAuthenticated={isAuthenticated}
                        refreshSession={refreshSession}
                      />
                    )}
                  </Suspense>
                </div>
              </div>
            </>
          </FeedbackButtonStateProvider>
        </WorkflowValidationsProvider>
      </ThemeProvider>
    </AssetCookie>
  )
}

export default App
